import { canAccessFeature, TierFeatureKey, normalizeSubscriptionTier } from '../constants/planFeatures';
import type { Academy, Lesson, Term } from '../types';
import { academyPublicBaseUrl, type AcademyPublicUrlOptions } from './academyPublicUrl';

function startOfUtcDay(d: Date): Date {
  const x = new Date(d);
  x.setUTCHours(0, 0, 0, 0);
  return x;
}

/** Term is still open for booking context: end date (calendar day) has not passed. */
export function isTermCurrentOrUpcoming(term: Pick<Term, 'endDate'>, now: Date = new Date()): boolean {
  const end = startOfUtcDay(new Date(term.endDate));
  const today = startOfUtcDay(now);
  return end.getTime() >= today.getTime();
}

/** Starter academies use public request booking (no self-service checkout). */
export function isStarterRequestBookingAcademy(
  academy: Pick<Academy, 'subscriptionTier'>
): boolean {
  const tier = normalizeSubscriptionTier(academy.subscriptionTier ?? null) ?? 'starter';
  return !canAccessFeature(tier, TierFeatureKey.PUBLIC_SELF_SERVICE_BOOKING);
}

/** Whether a lesson can deep-link to the public booking wizard (Professional+). */
export function isPublicLessonDirectBookable(
  lesson: Pick<Lesson, 'lessonType' | 'termId' | 'isActive'>,
  term: Pick<Term, 'id' | 'endDate'> | null | undefined,
  now: Date = new Date()
): boolean {
  if (!lesson.isActive) return false;

  if (lesson.lessonType === 'camp') return true;

  if (
    lesson.lessonType === 'group' &&
    lesson.termId &&
    term &&
    term.id === lesson.termId &&
    isTermCurrentOrUpcoming(term, now)
  ) {
    return true;
  }

  if (lesson.lessonType === 'private' && !lesson.termId) return true;

  if (
    lesson.lessonType === 'private' &&
    lesson.termId &&
    term &&
    term.id === lesson.termId &&
    isTermCurrentOrUpcoming(term, now)
  ) {
    return true;
  }

  return false;
}

/**
 * Public path for sharing a lesson (no origin).
 * Starter → lessons catalogue with request-booking deep link; Professional+ → book wizard when eligible.
 */
export function resolvePublicLessonShareSubpath(
  academy: Pick<Academy, 'subscriptionTier'>,
  lesson: Pick<Lesson, 'id' | 'lessonType' | 'termId' | 'isActive'>,
  term: Pick<Term, 'id' | 'endDate'> | null | undefined,
  now?: Date
): string {
  const anchor = `#lesson-${lesson.id}`;

  if (isStarterRequestBookingAcademy(academy)) {
    return `lessons?request=1${anchor}`;
  }

  if (isPublicLessonDirectBookable(lesson, term, now)) {
    return `book/${lesson.id}`;
  }

  return `lessons${anchor}`;
}

/** Absolute public URL for sharing a lesson (emails, copy link). */
export function resolvePublicLessonShareUrl(
  academy: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>,
  lesson: Pick<Lesson, 'id' | 'lessonType' | 'termId' | 'isActive'>,
  term: Pick<Term, 'id' | 'endDate'> | null | undefined,
  options?: AcademyPublicUrlOptions,
  now?: Date
): string {
  const subpath = resolvePublicLessonShareSubpath(academy, lesson, term, now);
  return academyPublicBaseUrl(academy, subpath, options, 'path');
}
